import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

/**
 * Risk burndown — spec 16.4.
 *
 * Tổng điểm rủi ro **còn mở** theo thời gian. Đường phải đi xuống; đi ngang
 * nghĩa là RAID log đang được ghi thêm mà không ai xử lý, và điều đó không nhìn
 * ra được từ bảng RAID (bảng đó chỉ biết hôm nay).
 *
 * Số rủi ro **đã xử lý** vẽ kèm để phân biệt hai lý do đường đi xuống: xử lý
 * xong thật, hay chỉ vì chưa ai ghi thêm rủi ro mới.
 */
export function RiskBurndownChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.riskBurndown(projectId, range),
    queryFn: () => reportApi.riskBurndown(projectId, range),
  });

  const buckets = report.data?.buckets ?? [];

  return (
    <ChartFrame
      title={t('reports.riskBurndownTitle')}
      help="riskBurndown"
      subtitle={t('reports.riskBurndownSubtitle')}
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && buckets.every((bucket) => bucket.exposure === 0 && bucket.retiredCount === 0)
          ? { title: t('reports.riskMatrixEmpty'), description: t('reports.riskMatrixEmptyHelp') }
          : null
      }
      legend={[
        { label: t('reports.riskExposure'), color: RAG_VAR.red },
        { label: t('reports.riskRetired'), color: FLOW_VAR.done },
      ]}
    >
      <div className="h-44 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={buckets}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <Tooltip
              contentStyle={{
                background: 'var(--color-background)',
                border: '1px solid var(--color-border)',
                fontSize: 12,
              }}
            />
            <Area
              type="monotone"
              dataKey="exposure"
              name={t('reports.riskExposure')}
              stroke={RAG_VAR.red}
              fill={RAG_VAR.red}
              fillOpacity={0.2}
            />
            <Line
              type="monotone"
              dataKey="retiredCount"
              name={t('reports.riskRetired')}
              stroke={FLOW_VAR.done}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}
